import {Component, OnInit, OnDestroy} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Standard} from '../../../../../models/standard.model';
import {NgForm} from '@angular/forms';
import {Response} from '@angular/http';
import {AdminService} from '../../../../../services/admin.service';
import { HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { isStandardVersionConflict, standardResponseWithVersion } from 'app/services/standard-version';

interface StandardInfoSnapshot extends Standard {
  translatedMainInfo?: boolean;
}
const HTTP_OK = 200;
const HTTP_NO_CONTENT = 204;
const LANGUAGE_RU = 'RU';
const LANGUAGE_EN = 'EN';
const LANGUAGE_ALL = 'ALL';

@Component({
  selector: 'app-edit-standard-info',
  templateUrl: './edit-standard-info.component.html',
  styleUrls: ['./edit-standard-info.component.css']
})
export class EditStandardInfoComponent implements OnInit, OnDestroy {
  didReload = false;
  info: any = {};
  standard: StandardInfoSnapshot;
  public successInfo = false;
  public errorInfo = false;
  public disableBtn = false;
  public versionConflict = false;
  public reloadFailed = false;
  public requiresReload = false;
  private originalStandard: StandardInfoSnapshot;
  private destroyed$ = new Subject<void>();

  translatedLanguage = '';

  constructor(public activeModal: NgbActiveModal,
              private adminService: AdminService) {
  }

  ngOnInit() {
  }

  setStandard(standard: StandardInfoSnapshot) {
    this.originalStandard = standard;
    this.standard = Object.assign({}, standard);
    this.resetInfo();
  }

  private resetInfo(): void {
    this.info.number = this.standard.number;
    this.info.ruName = this.standard.ruName;
    this.info.ruAnnotation = this.standard.ruAnnotation;


    this.info.enName = this.standard.enName;
    this.info.enAnnotation = this.standard.enAnnotation;

    this.info.translatedMainInfo = this.standard.translatedMainInfo;


    this.setTranslatedLanguage();
  }

  private setTranslatedLanguage(): void {
    if (this.info.ruName === null || this.info.ruAnnotation === null) {
      this.translatedLanguage = LANGUAGE_EN;
    } else if (this.info.enName === null || this.info.enAnnotation === null) {
      this.translatedLanguage = LANGUAGE_RU;
    } else {
      this.translatedLanguage = LANGUAGE_ALL;
    }

  }

  disableBtnByCharCount() {
    if (this.info.number !== null && this.info.number.length >= 3) {
      if (!this.info.translatedMainInfo || !this.notNullAllFields()) {
        // Valid number; One language
        return this.validOneLanguageFields();
      } else {
        // Two languages
        return this.validAllFields();
      }
    } else {
      // Number is not valid
      return false;
    }
  }

  editInfo() {
    if (this.disableBtn || this.versionConflict || this.requiresReload) { return; }
    this.successInfo = false;
    this.errorInfo = false;
    this.disableBtn = true;

    if (this.info.translatedMainInfo ? this.validAllFields() : this.validOneLanguageFields()) {
      this.doEditing();
    } else {
      this.errorInfo = true;
      this.disableBtn = false;
    }
  }

  private validAllFields() {
    let allValid = true;
    allValid = this.info.number !== null && this.info.number.length >= 3
      && this.info.ruName !== null && this.info.ruName.length >= 3
      && this.info.ruAnnotation !== null && this.info.ruAnnotation.length >= 3
      && this.info.enName !== null && this.info.enName.length >= 3
      && this.info.enAnnotation !== null && this.info.enAnnotation.length >= 3;
    return allValid;
  }

  private validOneLanguageFields() {
    if (this.translatedLanguage === 'RU') {
      // RUSSIAN
      return this.info.ruName !== null && this.info.ruName.length >= 3
        && this.info.ruAnnotation !== null && this.info.ruAnnotation.length >= 3;
    } else if (this.translatedLanguage === 'EN') {
      // ENGLISH
      return this.info.enName !== null && this.info.enName.length >= 3
        && this.info.enAnnotation !== null && this.info.enAnnotation.length >= 3;
    } else {
      return this.validAllFields();
    }
  }

  private doEditing() {
    const expectedVersion = this.standard.version;
    const submittedInfo = Object.assign({}, this.info);
    this.adminService.editStandardInfo(this.standard, submittedInfo).takeUntil(this.destroyed$).subscribe(
      response => this.completeEditing(response, expectedVersion, submittedInfo),
      error => this.failEditing(error)
    );
  }

  private completeEditing(response: HttpResponse<Object>, expectedVersion: number, submittedInfo: Object): void {
    this.disableBtn = false;
    if (response.status !== HTTP_OK && response.status !== HTTP_NO_CONTENT) { return; }
    Object.assign(this.standard, submittedInfo);
    if (this.originalStandard.version === expectedVersion) {
      Object.assign(this.originalStandard, submittedInfo, { version: this.standard.version });
    }
    this.successInfo = true;
    this.errorInfo = false;
  }

  private failEditing(error): void {
    this.versionConflict = isStandardVersionConflict(error);
    this.requiresReload = true;
    this.errorInfo = true;
    this.successInfo = false;
    this.disableBtn = false;
  }

  reloadStandard(): void {
    if (this.disableBtn) { return; }
    this.disableBtn = true;
    this.reloadFailed = false;
    this.adminService.getStandard(this.standard.id).takeUntil(this.destroyed$).subscribe(
      response => this.acceptReload(response),
      () => { this.reloadFailed = true; this.disableBtn = false; }
    );
  }

  private acceptReload(response: HttpResponse<Object>): void {
    this.disableBtn = false;
    if (response.status !== HTTP_OK || !response.body) { this.reloadFailed = true; return; }
    const standard = standardResponseWithVersion(response as HttpResponse<Standard>).body;
    if (standard.id !== this.standard.id || standard.version == null) { this.reloadFailed = true; return; }
    this.didReload = true;
    Object.assign(this.originalStandard, standard);
    this.setStandard(this.originalStandard);
    this.versionConflict = false;
    this.requiresReload = false;
    this.errorInfo = false;
    this.successInfo = false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private notNullAllFields() {
    return this.info.ruName !== null
      && this.info.ruAnnotation !== null
      && this.info.enName !== null
      && this.info.enAnnotation !== null;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-standard/popups/edit-standard-info/edit-standard-info.component.ts